import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient, { setUnauthorizedHandler, TOKEN_KEY, ROLE_KEY } from '../api/client';

export type AuthRole = 'admin' | 'kitchen';

interface AuthContextType {
  token: string | null;
  role: AuthRole | null;
  isAuthenticated: boolean;
  login: (passcode: string, role: AuthRole) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [role, setRole] = useState<AuthRole | null>(() => localStorage.getItem(ROLE_KEY) as AuthRole | null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    setToken(null);
    setRole(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(logout);
  }, [logout]);

  // Same-origin staff tabs share localStorage: reconcile React state whenever
  // another tab logs in, logs out, or switches role, so no tab keeps showing
  // the old role or reuses a stale token (the kitchen socket effect keys on
  // `token`, so it is recreated with the fresh credentials too).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== TOKEN_KEY && event.key !== ROLE_KEY && event.key !== null) return;
      const nextToken = localStorage.getItem(TOKEN_KEY);
      const nextRole = localStorage.getItem(ROLE_KEY) as AuthRole | null;
      setToken(prev => (prev !== nextToken ? nextToken : prev));
      setRole(prev => (prev !== nextRole ? nextRole : prev));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (passcode: string, role: AuthRole) => {
    const res = await apiClient.post('/auth/login', { passcode, role });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    localStorage.setItem(ROLE_KEY, res.data.role);
    setToken(res.data.token);
    setRole(res.data.role);
  };

  return (
    <AuthContext.Provider value={{ token, role, isAuthenticated: !!token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
